import { Mail, Phone, MessageCircle, MapPin, Clock } from "lucide-react";
import { BRAND } from "../data/site";
import { useSEO } from "../lib/seo";
import LeadForm from "../components/LeadForm";

export default function Contact() {
    useSEO({
        title: "Contact RP Traders — Indian Spices Exporter, Guntur",
        description:
            "Get in touch with RP Traders' export desk. Reach us on WhatsApp +91 93473 04584, email info@rptradersiec.com or visit our Guntur office. We respond within 24 hours.",
        keywords:
            "Contact RP Traders, Indian spices exporter contact, Guntur spice supplier",
    });
    return (
        <main className="bg-white pt-32 pb-24 sm:pt-40">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
                <span className="text-xs uppercase tracking-[0.22em] text-brand-red">Contact</span>
                <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold leading-[1.04] tracking-[-0.03em] sm:text-6xl">
                    Talk to our export desk.
                </h1>
                <p className="mt-4 flex items-center gap-2 text-sm text-ink-muted">
                    <Clock className="h-4 w-4 text-brand-red" />
                    We respond to export enquiries within 24 hours.
                </p>

                <div className="mt-12 grid gap-10 lg:grid-cols-12">
                    <div className="grid gap-4 lg:col-span-5">
                        <ContactCard
                            icon={MessageCircle}
                            title="WhatsApp (Export Team)"
                            value="+91 94910 85798"
                            href={`https://wa.me/${BRAND.leadWhatsapp}`}
                            tone="turmeric"
                            testid="cp-wa"
                        />
                        <ContactCard icon={Mail} title="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} testid="cp-email" />
                        <ContactCard icon={Phone} title="Phone" value={BRAND.phone} href={`tel:+${BRAND.phoneIntl}`} testid="cp-phone" />
                        <div className="rounded-3xl border border-hairline bg-neutral-bg p-6">
                            <div className="flex items-start gap-3">
                                <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-red/10 text-brand-red">
                                    <MapPin className="h-5 w-5" />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-ink-muted">Head Office · Guntur</div>
                                    <div className="mt-1 text-sm leading-relaxed text-ink">{BRAND.head}</div>
                                </div>
                            </div>
                            <div className="mt-5 flex items-start gap-3">
                                <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-turmeric/15 text-brand-turmericDeep">
                                    <MapPin className="h-5 w-5" />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-ink-muted">Branch Office · Hyderabad</div>
                                    <div className="mt-1 text-sm leading-relaxed text-ink">{BRAND.branch}</div>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-hidden rounded-3xl border border-hairline">
                            <iframe
                                title="RP Traders, Guntur head office"
                                src={BRAND.mapEmbed}
                                width="100%"
                                height="280"
                                style={{ border: 0 }}
                                loading="lazy"
                                allowFullScreen
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="cp-map"
                            />
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <h2 className="font-display text-2xl font-bold tracking-tight">Business enquiry form</h2>
                        <p className="mt-2 text-sm text-ink-muted">
                            Share your requirement and our export desk will reply within one business day.
                        </p>
                        <div className="mt-5">
                            <LeadForm source="contact_page" />
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

function ContactCard({ icon: Icon, title, value, href, tone, testid }) {
    const accent = tone === "turmeric"
        ? "bg-brand-turmeric/15 text-brand-turmericDeep"
        : "bg-brand-red/10 text-brand-red";
    return (
        <a
            href={href}
            data-testid={testid}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            className="group flex items-center gap-4 rounded-3xl border border-hairline bg-white p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg"
        >
            <span className={`grid h-12 w-12 place-items-center rounded-full ${accent}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.18em] text-ink-muted">{title}</div>
                <div className="font-display text-lg font-semibold text-ink">{value}</div>
            </div>
        </a>
    );
}
